/**
 * Routines — 1ᵉʳ sous-onglet de **Musculation**
 * (`ARCHITECTURE_V2.md` §4.1, §4.3 et §4.6, specs §8.2 et §12.2).
 *
 * ⚠️ **Les routines sont ici, pas dans la Bibliothèque.** C'est le §8.2 des
 * specs qui tranche (« Routines, lancement et suivi de séance ») et l'usage qui
 * le confirme : on ouvre une routine pour **la faire**, pas pour la consulter.
 * Depuis les retours du 28/08/2026, les **exercices** l'ont rejointe pour la
 * même raison — cf. l'en-tête d'`ExercisesSection.tsx`.
 *
 * ⚠️ **La séance en direct n'existe pas encore** : le §8 en fait l'étape 6. Le
 * bouton « Démarrer la séance » est **désactivé et il le dit** — plutôt
 * qu'absent, parce que sa place fait partie de ce que l'étape 5 doit valider,
 * et plutôt qu'actif-sans-effet, parce qu'un bouton qui ne fait rien est un
 * défaut qu'on met des mois à comprendre.
 *
 * 🔴 **La recherche est locale, comme les trois autres domaines** (§3.1,
 * étape 4). Une seule entrée de cache (`ROUTINES_KEY`), persistée, filtrée en
 * mémoire par `lib/localSearch.ts` avec le classement de `shared/search.ts` —
 * le **même** module que le serveur. En salle, chercher une routine se comporte
 * donc exactement comme à la maison : il ne se passe *rien de différent*.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import type {
  RoutineBlockDTO,
  RoutineDTO,
  RoutineLineDTO,
  WorkoutSessionDTO,
} from '../../../../shared/types';
import { isTransportFailure, readError } from '../../api/client';
import { EXERCISES_KEY, fetchExercises } from '../../api/exercises';
import { putSession, putSessionPlan } from '../../api/sessions';
import {
  deleteRoutine,
  fetchRoutines,
  patchRoutine,
  putRoutine,
  ROUTINES_KEY,
} from '../../api/routines';
import { Button, Card, GlyphPlay, GlyphSearch, GlyphStar, TextField } from '../../components/nourri';
import { ErrorNote, Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { useOpenSession } from '../../hooks/useOpenSession';
import { writeSession } from '../../hooks/useSession';
import { browserToday, newClientId } from '../../lib/dates';
import { routineWeight, searchRoutinesLocally } from '../../lib/localSearch';
import { BodyMap } from './BodyMap';
import { ExerciseSheet } from './ExerciseSheet';
import { ExerciseThumb } from './ExerciseThumb';
import { RoutineEditorSheet } from './RoutineEditor';
import { muscleIntensity, muscleVolume } from './muscleVolume';
import { planOfRoutine } from './sessionPlan';
import {
  BLOCK_KIND_LABEL,
  draftOfRoutine,
  emptyRoutineDraft,
  formatMeasure,
  formatSeconds,
  inputOfRoutineDraft,
  SET_KIND_LABEL,
  type RoutineDraft,
} from './routineDraft';

type Editing = { clientId: string; title: string };

/* ==========================================================================
 * Lecture d'un bloc
 * ======================================================================= */

/**
 * En-tête d'un bloc, tel qu'on le lira en salle.
 *
 * ⚠️ **Le libellé dit ce que le bloc fait faire**, pas seulement son nom :
 * « Circuit · 3 tours · repos 2 min » se lit d'un coup d'œil, « circuit » seul
 * obligerait à ouvrir l'éditeur pour savoir combien de tours. Et le repos n'est
 * cité qu'ici — jamais sur une ligne — parce que c'est une propriété du bloc
 * (§4.3), et qu'une même valeur écrite à deux endroits finit par en dire deux.
 *
 * 🔴 **Un bloc `single` ne s'annonce plus.** Depuis les retours du 28/08, un
 * exercice s'ajoute directement à la routine ; le bloc à une ligne n'est plus
 * qu'un emballage interne, et lui coller l'étiquette « Exercice seul » ferait
 * apparaître à l'écran une notion que l'interface ne propose plus. Il ne reste
 * donc que son repos, quand il en a un.
 */
function BlockHeader({ block }: { block: RoutineBlockDTO }) {
  const repos = formatSeconds(block.restSeconds);
  const parts = [
    block.kind === 'single' ? null : BLOCK_KIND_LABEL[block.kind],
    block.kind === 'circuit' && block.rounds !== null ? `${String(block.rounds)} tours` : null,
    repos === null ? null : `repos ${repos}`,
  ].filter((item): item is string => item !== null);

  if (parts.length === 0) return null;
  return <p className="n-eyebrow">{parts.join(' · ')}</p>;
}

/**
 * Une ligne, en lecture.
 *
 * ⚠️ **`exerciseName` vient du serveur, qui l'a joint à la lecture** — il n'est
 * pas copié dans la routine (§4.1). Renommer un exercice change donc ce que
 * cette ligne affiche, ce qui est le comportement d'un **référentiel**. C'est
 * l'inverse exact du journal, dont chaque entrée a figé son libellé.
 */
function LineRow({ line, onOpen }: { line: RoutineLineDTO; onOpen: () => void }) {
  const details = [
    line.targetWeightKg === null ? null : `${String(line.targetWeightKg)} kg`,
    line.targetRpe === null ? null : `RPE ${String(line.targetRpe)}`,
    line.setKind === 'work' ? null : SET_KIND_LABEL[line.setKind],
  ].filter((item): item is string => item !== null);

  return (
    <li className="n-routine-read-line">
      {/*
        🔴 **La vignette ici aussi** (demandé après usage, 29/08/2026). C'est la
        vue qu'on ouvre avant de partir en salle : reconnaître un mouvement à sa
        silhouette y vaut autant que dans le sélecteur.

        ⚠️ **Et c'est ce qui met les images de la routine au cache.** Le §1.1
        annonce un préchargement **ciblé** à l'ouverture d'une routine
        (~8 exercices) ; ouvrir cette vue en fait déjà la moitié — la première
        vue de chaque exercice. L'étape 6 n'aura plus qu'à compléter avec la
        seconde, sans rien avoir à décider de neuf.

        ⚠️ **Un exercice sans image ne réserve toujours aucun espace** (§1.1) :
        `ExerciseThumb` ne rend rien, et la rangée se referme dessus.
        `RoutineLineDTO` porte `exerciseMediaKey` et `exerciseImageCount`
        exactement pour permettre ça sans second aller-retour.
      */}
      {/*
        🔴 **La fiche s'ouvre d'ici aussi** *(29/08/2026, demandé après usage)*.
        C'est la vue qu'on lit **avant** de partir en salle : y répondre à
        « c'est quoi, ce mouvement ? » évite d'avoir à aller le chercher dans
        l'onglet Exercices, puis à revenir. La même feuille qu'en séance —
        extraite pour ça (`ExerciseFacts`), pas recopiée.

        ⚠️ **Toute la ligne est la cible**, vignette et détails compris : on
        vise un exercice, pas un mot. Et c'est ce qui évite deux cibles voisines
        qui se disputeraient l'annonce du lecteur d'écran.
      */}
      <button type="button" className="n-routine-read-line__open" onClick={onOpen}>
        <ExerciseThumb mediaKey={line.exerciseMediaKey} imageCount={line.exerciseImageCount} />
        <span className="n-routine-read-line__main">
          <span className="n-list__name">{line.exerciseName}</span>
          <span className="n-list__meta tnum">
            {formatMeasure(line)}
            {details.length > 0 && ` · ${details.join(' · ')}`}
          </span>
          {line.note !== null && <span className="n-list__meta">{line.note}</span>}
        </span>
      </button>
    </li>
  );
}

/* ==========================================================================
 * Vue de détail
 * ======================================================================= */

function RoutineDetail({
  routine,
  onEdit,
  onDelete,
  onStart,
  removing,
  starting,
  enCours,
}: {
  routine: RoutineDTO;
  onEdit: () => void;
  onDelete: () => void;
  onStart: () => void;
  removing: boolean;
  starting: boolean;
  /** La séance déjà ouverte, s'il y en a une — cf. `useOpenSession`. */
  enCours: WorkoutSessionDTO | null;
}) {
  const poids = routineWeight(routine);

  /*
   * ⚠️ **La même entrée de cache que la bibliothèque et le sélecteur**
   * (`EXERCISES_KEY`, §3.1) : ouvrir une routine ne déclenche aucun appel
   * supplémentaire, et hors ligne elle lit la réplique restaurée par
   * `lib/persist.ts`.
   */
  const exercises = useQuery({ queryKey: EXERCISES_KEY, queryFn: fetchExercises });
  const charges = useMemo(
    () => muscleVolume(routine, exercises.data ?? []),
    [routine, exercises.data],
  );
  const intensites = useMemo(() => muscleIntensity(charges), [charges]);

  /* Une étape par ligne — la forme de `recipe.steps` (étape 7). */
  const etapesEchauffement = (routine.warmup ?? '')
    .split('\n')
    .map((ligne) => ligne.trim())
    .filter((ligne) => ligne !== '');

  /*
   * ⚠️ **Le nom copié voyage avec l'adresse** : une routine ne peut pas citer
   * un exercice supprimé (`RESTRICT`), mais la feuille prend le même contrat
   * que depuis la séance — elle sait afficher un titre sans fiche.
   */
  const [fiche, setFiche] = useState<{ clientId: string; name: string } | null>(null);

  return (
    <div className="n-page">
      <p className="n-hint" style={{ margin: 0 }}>
        {String(poids.blocks)} bloc{poids.blocks > 1 ? 's' : ''} · {String(poids.exercises)}{' '}
        exercice{poids.exercises > 1 ? 's' : ''} · {String(poids.sets)} série
        {poids.sets > 1 ? 's' : ''}
      </p>

      {routine.notes !== null && (
        <Card>
          <p style={{ margin: 0 }}>{routine.notes}</p>
        </Card>
      )}

      {/*
        🔴 **L'échauffement AVANT les blocs** *(29/08/2026)*, et c'est tout ce
        que sa position a besoin de dire : on le fait avant. Le mettre en bas,
        ou dans un repli, en ferait une information qu'on consulte — alors que
        c'est une consigne qu'on suit.

        ⚠️ **Une étape par ligne**, comme les étapes d'une recette : le texte
        est libre, la numérotation est de l'affichage. Rien n'est stocké d'elle.
      */}
      {etapesEchauffement.length > 0 && (
        <Card>
          <p className="n-eyebrow">Échauffement</p>
          <ol className="n-exo__steps">
            {etapesEchauffement.map((etape, index) => (
              <li key={index}>{etape}</li>
            ))}
          </ol>
        </Card>
      )}

      {/*
        🔴 **La carte corporelle** *(29/08/2026, demandé après usage)*. Elle se
        dérive de la **réplique** d'exercices (`muscleVolume`), pas d'une route :
        elle fonctionne donc sans réseau, et ne peut pas se désaccorder de la
        liste qu'elle résume — c'est le raisonnement d'`exerciseFacets` à
        l'étape 4.

        ⚠️ Elle ne s'affiche que si la routine porte des exercices dont on
        connaît les muscles : une routine vide, ou faite d'exercices persos sans
        muscles saisis, n'a rien à montrer et ne réserve aucun espace (§1.1).
      */}
      {charges.length > 0 && (
        <Card>
          <p className="n-eyebrow">Muscles travaillés</p>
          <BodyMap loads={charges} intensite={intensites} />
        </Card>
      )}

      {routine.blocks.length === 0 && (
        <p className="n-empty">
          Cette routine n’a pas encore d’exercice. Elle s’enregistre quand même — c’est une
          routine qu’on compose.
        </p>
      )}

      {routine.blocks.map((block) => (
        <Card key={block.clientId}>
          <BlockHeader block={block} />
          <ul className="n-routine-read">
            {block.lines.map((line) => (
              <LineRow
                key={line.clientId}
                line={line}
                onOpen={() => {
                  setFiche({ clientId: line.exerciseClientId, name: line.exerciseName });
                }}
              />
            ))}
          </ul>
        </Card>
      ))}

      {/*
        ✅ **Activé à l'étape 6.** Il était désactivé et le disait, plutôt
        qu'absent — c'était le pari de l'étape 5 : « sa place fait partie de ce
        que l'étape 5 doit valider ». L'usage l'a confirmée, il n'a pas bougé.

        🔴 **Une routine sans bloc ne se lance pas**, et c'est l'autre moitié de
        l'asymétrie posée à l'étape 5 : une routine vide **s'enregistre** (c'est
        une routine qu'on compose), mais elle est irrecevable **à l'emploi** —
        et l'emploi d'une routine, c'est précisément cet écran. Le refus est
        donc ici, à l'endroit exact que `checkBlocks` annonçait.
      */}
      {/*
        🔴 **Une seule séance à la fois** *(31/08/2026, demandé après usage)*.
        Tant qu'une séance est ouverte, on ne peut pas en lancer une seconde :
        deux séances en cours rendraient l'historique illisible — laquelle
        porte le travail qu'on vient de faire ? — et le rappel du dernier
        passage devrait choisir entre elles.

        ⚠️ **Le bouton reste VISIBLE et dit pourquoi**, plutôt que de
        disparaître : un bouton absent se lit comme une fonction perdue. C'est
        le pari déjà tenu à l'étape 5 pour la routine vide, et l'usage l'a
        confirmé.
      */}
      <Button
        variant="primary"
        size="lg"
        block
        disabled={routine.blocks.length === 0 || starting || enCours !== null}
        onClick={onStart}
      >
        Démarrer la séance
      </Button>
      {enCours !== null && (
        <Hint>
          Une séance est déjà en cours{enCours.routineName === null ? '' : ` (${enCours.routineName})`}.
          Terminez-la pour en lancer une autre — le rappel en bas d’écran y ramène.
        </Hint>
      )}
      {routine.blocks.length === 0 && (
        <Hint>
          Ajoutez au moins un exercice : une routine vide s’enregistre, mais il n’y a rien à
          dérouler.
        </Hint>
      )}

      <ExerciseSheet
        clientId={fiche?.clientId ?? null}
        fallbackName={fiche?.name ?? ''}
        onClose={() => {
          setFiche(null);
        }}
      />

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Button variant="secondary" shape="rounded" block onClick={onEdit}>
          Modifier
        </Button>
        <Button variant="danger" shape="rounded" block disabled={removing} onClick={onDelete}>
          Supprimer
        </Button>
      </div>
    </div>
  );
}

/* ==========================================================================
 * La section
 * ======================================================================= */

export function RoutinesSection({
  /**
   * La routine ouverte, **lue dans l'URL** (`/musculation/routine/:clientId`).
   *
   * 🔴 **C'était un `useState`, et c'était un défaut** *(corrigé le
   * 31/08/2026, relevé à l'usage)*. Toucher l'onglet Musculation depuis une
   * routine ne ramenait pas à la liste : la barre naviguait pourtant bien vers
   * `/musculation`, mais ce composant n'était pas démonté pour autant — même
   * route, même position — donc l'état survivait et l'écran ne bougeait pas.
   *
   * ⚠️ C'est l'invariant de l'étape 5 de la V1 (« sections et sous-pages sont
   * des ROUTES ») dans son **symptôme inverse** : il y était écrit qu'un état
   * local disparaît « au moment précis où il faudrait s'en souvenir » ; ici il
   * persistait au moment précis où il fallait l'oublier.
   *
   * ⚠️ **`editing` reste local, et ce n'est pas une inconséquence** : c'est une
   * feuille modale, pas une sous-page. La règle porte sur ce qui a une adresse.
   */
  openedClientId,
}: {
  openedClientId: string | null;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  /*
   * 🔴 **Une seule séance à la fois** *(31/08/2026)*. Le verrou est **client**,
   * jamais serveur : un refus d'API serait un 4xx, donc `failed` définitif pour
   * la file (§3.3) — une séance composée hors ligne et drainée trois heures
   * plus tard disparaîtrait pour un conflit qui n'existait pas au moment du
   * geste. Cf. `useOpenSession`, où le raisonnement est écrit en entier.
   */
  const enCours = useOpenSession();
  const [term, setTerm] = useState('');
  const [favoriteOnly, setFavoriteOnly] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [draft, setDraft] = useState<RoutineDraft>(emptyRoutineDraft);

  /*
   * 🔴 **Une seule requête, sans le terme dans la clé** — le fond de la
   * recherche hors ligne (étape 4). Pas de débounce non plus : il n'existait
   * que pour épargner des appels réseau, et il n'y en a plus.
   */
  const routines = useQuery({ queryKey: ROUTINES_KEY, queryFn: fetchRoutines });

  const liste = useMemo(() => routines.data ?? [], [routines.data]);
  const resultats = useMemo(
    () => searchRoutinesLocally(liste, term, favoriteOnly),
    [liste, term, favoriteOnly],
  );
  /*
   * ⚠️ **`null` si l'adresse ne désigne rien** — routine supprimée, lien
   * partagé devenu caduc, réplique pas encore chargée. On retombe alors sur la
   * liste plutôt que sur un écran vide : `/musculation/routine/inconnue` reste
   * une adresse qui montre quelque chose. Même normalisation qu'une section
   * inconnue dans `MusculationScreen`.
   */
  const ouverte = liste.find((row) => row.clientId === openedClientId) ?? null;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ROUTINES_KEY });
  };

  const save = useMutation({
    mutationFn: () => {
      if (!editing) throw new Error('Aucune routine en cours d’édition.');
      return putRoutine(editing.clientId, inputOfRoutineDraft(draft));
    },
    onSuccess: () => {
      refresh();
      setEditing(null);
    },
  });

  const favorite = useMutation({
    mutationFn: (row: RoutineDTO) => patchRoutine(row.clientId, { isFavorite: !row.isFavorite }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: deleteRoutine,
    onSuccess: () => {
      refresh();
      /*
       * ⚠️ **Revenir à la liste, et non « fermer le détail »** : depuis que
       * l'ouverture est une route, l'adresse `/musculation/routine/<supprimée>`
       * ne désigne plus rien. La laisser dans l'historique ferait revenir sur
       * une routine absente au premier glissement de retour.
       */
      void navigate('/musculation', { replace: true });
    },
  });

  /**
   * Démarrer une séance (V2 étape 6, §4.5).
   *
   * 🔴 **Tout se fait localement, et rien n'attend le serveur.** Le plan est
   * déroulé depuis la routine qu'on tient déjà (`planOfRoutine`), les identités
   * sont tirées ici (§2.3-b), la séance est écrite dans le cache **avant** que
   * la moindre requête ne parte, et l'écran s'ouvre. En mode avion, la
   * navigation est immédiate et l'intégralité de la séance part en file.
   *
   * 🔴 **L'en-tête est écrit AVANT les séries, et l'ordre n'est pas
   * indifférent** — mais pas pour la raison qu'on croit. La file draine
   * séquentiellement, donc le `PUT` de la séance précède ceux des séries : le
   * serveur ne reçoit jamais une série avant sa séance. Ce n'est pourtant pas
   * une **dépendance** au sens du §3.3 — il n'y a ni place-titre ni `dependsOn`,
   * puisque toutes les identités sont connues d'avance. C'est le simple fait
   * que l'ordre d'émission soit conservé qui suffit.
   *
   * ⚠️ **`date` vient de l'appareil, `startedAt` aussi** (cf. `SessionInput`) :
   * une séance commencée à 18 h en mode avion et drainée à 21 h doit afficher
   * 18 h. Prendre l'heure du serveur la daterait de son drainage.
   */
  /**
   * Démarrer une séance (V2 étape 6, §4.5).
   *
   * 🔴 **L'écran s'ouvre AVANT que quoi que ce soit ne parte sur le réseau**
   * *(corrigé le 29/08/2026, après usage : « environ 25 s avant d'afficher
   * l'écran »)*.
   *
   * Le premier jet attendait le `PUT` de la séance **puis un `PUT` par série**
   * avant de naviguer : une trentaine d'allers-retours séquentiels. Le défaut
   * n'était pas la lenteur du réseau, c'était d'avoir **fait dépendre
   * l'ouverture de l'écran du serveur** — dans l'écran précisément conçu pour
   * ne rien lui devoir. Toute l'architecture de l'étape était déjà là ; on ne
   * s'en servait pas.
   *
   * L'ordre est donc : écrire le cache, **naviguer**, puis envoyer.
   *
   * 🔴 **Et le plan part en UNE opération** (`putSessionPlan`), plus série par
   * série. Ce n'est pas qu'une économie : envoyer trente `PUT` en tâche de fond
   * aurait suffi à rendre l'écran instantané, mais `emittedAt` est posé à
   * l'**émission** — la 20ᵉ série du plan, émise vingt secondes plus tard,
   * aurait porté un horodatage **postérieur** à la validation d'une série que
   * l'utilisateur venait de cocher, et l'aurait repassée en `pending`. Une
   * seule opération supprime la course.
   *
   * ⚠️ **`date` et `startedAt` viennent de l'appareil** (cf. `SessionInput`) :
   * une séance commencée à 18 h en mode avion et drainée à 21 h doit afficher
   * 18 h. Prendre l'heure du serveur la daterait de son drainage.
   */
  const start = useMutation({
    mutationFn: async (row: RoutineDTO) => {
      const clientId = newClientId();
      const startedAt = Date.now();
      const date = browserToday();
      const plan = planOfRoutine(row, newClientId);

      /*
       * ⚠️ **L'aperçu d'abord.** `putSession` lèvera `QueuedError` hors ligne ;
       * si l'on écrivait le cache après, on n'écrirait jamais rien. C'est le
       * même piège que le rollback non scindé de l'étape 3 — hors ligne, une
       * mutation « échoue » alors qu'elle est parfaitement en route.
       */
      writeSession(queryClient, {
        clientId,
        id: 0,
        date,
        routineClientId: row.clientId,
        routineName: row.name,
        startedAt,
        finishedAt: null,
        notes: null,
        notesByExercise: [],
        createdAt: startedAt,
        updatedAt: startedAt,
        sets: plan.map((planned) => ({
          clientId: planned.clientId,
          id: 0,
          position: planned.body.position,
          exerciseClientId: planned.body.exerciseClientId,
          exerciseName: planned.body.exerciseName,
          /*
           * 🔴 **La vignette dès l'aperçu, pas à la première réponse du
           * serveur.** La version précédente écrivait `null` ici en pariant sur
           * le drainage : à l'usage, les images n'apparaissaient qu'**après
           * avoir validé la première série**, et hors ligne jamais. Le plan la
           * porte (`PlannedSet.media`), lue dans la routine où elle était
           * disponible depuis l'étape 5.
           */
          exerciseMediaKey: planned.media.mediaKey,
          exerciseImageCount: planned.media.imageCount,
          blockKey: planned.body.blockKey ?? null,
          setIndex: planned.body.setIndex,
          setKind: planned.body.setKind ?? 'work',
          reps: null,
          durationSeconds: null,
          weightKg: null,
          rpe: null,
          status: 'pending' as const,
          targetRepsMin: planned.body.targetRepsMin ?? null,
          targetRepsMax: planned.body.targetRepsMax ?? null,
          targetDurationSeconds: planned.body.targetDurationSeconds ?? null,
          targetWeightKg: planned.body.targetWeightKg ?? null,
          targetRpe: planned.body.targetRpe ?? null,
        })),
      });

      /*
       * 🔴 **Les deux écritures partent EN TÂCHE DE FOND, enchaînées.**
       * Enchaînées parce que le plan a besoin de sa séance ; en tâche de fond
       * parce que l'écran, lui, n'a besoin d'aucune des deux — il lit le cache
       * qu'on vient d'écrire.
       *
       * ⚠️ **`QueuedError` est un SUCCÈS de mise en file**, pas un échec : hors
       * ligne, les deux opérations sont en route. Laisser remonter
       * interromprait la chaîne après la première.
       */
      void (async () => {
        const enFile = async (envoi: () => Promise<unknown>) => {
          try {
            await envoi();
            return true;
          } catch (error) {
            if (isTransportFailure(error as Error)) return true;
            throw error;
          }
        };

        try {
          await enFile(() =>
            putSession(clientId, {
              date,
              routineClientId: row.clientId,
              routineName: row.name,
              startedAt,
            }),
          );
          await enFile(() =>
            putSessionPlan(clientId, {
              sets: plan.map((planned) => ({ clientId: planned.clientId, ...planned.body })),
            }),
          );
        } catch {
          /*
           * ⚠️ **Rien à faire ici, et c'est délibéré.** Un refus applicatif se
           * verra dans Réglages → Synchronisation, avec son motif et son
           * « Réessayer » (§3.3). L'écran de séance, lui, a déjà tout ce qu'il
           * lui faut : le remonter maintenant ferait apparaître une erreur sur
           * une séance qui se déroule parfaitement.
           */
        }
      })();

      return clientId;
    },
    onSuccess: (clientId) => {
      void navigate(`/musculation/seance/${clientId}`);
    },
  });

  const editor = editing && (
    <RoutineEditorSheet
      key={editing.clientId}
      open
      title={editing.title}
      draft={draft}
      onDraftChange={setDraft}
      saving={save.isPending}
      onSubmit={() => {
        save.mutate();
      }}
      onClose={() => {
        setEditing(null);
      }}
    />
  );

  /* --- Vue détail ------------------------------------------------------ */

  if (ouverte) {
    return (
      <div className="n-page">
        <Button
          variant="ghost"
          shape="rounded"
          onClick={() => {
            void navigate('/musculation');
          }}
        >
          ← Toutes les routines
        </Button>
        {/*
          ⚠️ Un `h3` et non un `ScreenTitle` : le titre de l'écran est
          « Musculation », posé une fois par la coque avec les sous-onglets.
          Même découpage que le détail d'un exercice.
        */}
        <h3 className="n-exo__title">{ouverte.name}</h3>

        {remove.isError && <ErrorNote>{remove.error.message}</ErrorNote>}
        {save.isError && <ErrorNote>{save.error.message}</ErrorNote>}

        {start.isError && !isTransportFailure(start.error) && (
          <ErrorNote>{start.error.message}</ErrorNote>
        )}

        <RoutineDetail
          routine={ouverte}
          removing={remove.isPending}
          starting={start.isPending}
          enCours={enCours}
          onStart={() => {
            start.mutate(ouverte);
          }}
          onEdit={() => {
            setDraft(draftOfRoutine(ouverte));
            setEditing({ clientId: ouverte.clientId, title: `Modifier « ${ouverte.name} »` });
          }}
          onDelete={() => {
            remove.mutate(ouverte.clientId);
          }}
        />

        {editor}
      </div>
    );
  }

  /* --- Liste ----------------------------------------------------------- */

  return (
    <div className="n-page">
      <TextField
        shape="pill"
        lead={<GlyphSearch />}
        aria-label="Chercher une routine ou un exercice qu’elle contient"
        placeholder="Chercher une routine, un exercice…"
        value={term}
        onChange={(event) => {
          setTerm(event.target.value);
        }}
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        selectOnFocus={false}
      />

      {liste.length > 0 && (
        <div className="n-chip-row">
          <Button
            variant={favoriteOnly ? 'selected' : 'secondary'}
            aria-pressed={favoriteOnly}
            onClick={() => {
              setFavoriteOnly((current) => !current);
            }}
          >
            ★ Favorites
          </Button>
        </div>
      )}

      {routines.isPending && <Hint>Chargement…</Hint>}
      <ReadNote error={readError(routines)} quoi="les routines" />
      {save.isError && <ErrorNote>{save.error.message}</ErrorNote>}
      {favorite.isError && <ErrorNote>{favorite.error.message}</ErrorNote>}
      {/*
        ⚠️ **Le refus de démarrage se dit AUSSI dans la liste** depuis qu'on
        peut y lancer une séance : l'oublier ici laisserait le raccourci
        échouer en silence, alors que le détail, lui, l'annonçait.

        ⚠️ **`isTransportFailure` exclu**, comme dans le détail : hors ligne,
        une séance se compose parfaitement et part en file. Un bandeau rouge
        dirait le contraire — « ne jamais présenter “hors ligne” comme une
        erreur » (§3.5).
      */}
      {start.isError && !isTransportFailure(start.error) && (
        <ErrorNote>{start.error.message}</ErrorNote>
      )}

      {!routines.isPending && liste.length === 0 && readError(routines) === null && (
        <p className="n-empty">
          Aucune routine. Composez-en une : des exercices à la suite, et des blocs quand
          plusieurs s’enchaînent en superset ou en circuit.
        </p>
      )}

      {liste.length > 0 && resultats.length === 0 && (
        <p className="n-empty">Aucune routine ne correspond.</p>
      )}

      {resultats.length > 0 && (
        <Card padding="none">
          <ul className="n-list">
            {resultats.map((row) => {
              const poids = routineWeight(row);
              return (
                <li key={row.clientId} className="n-list__row">
                  <Button
                    variant="ghost"
                    shape="circle"
                    className={row.isFavorite ? 'n-star' : 'n-star n-star--off'}
                    aria-pressed={row.isFavorite}
                    aria-label={
                      row.isFavorite
                        ? `Retirer ${row.name} des favorites`
                        : `Mettre ${row.name} en favorite`
                    }
                    onClick={() => {
                      favorite.mutate(row);
                    }}
                  >
                    <GlyphStar filled={row.isFavorite} />
                  </Button>

                  <button
                    type="button"
                    className="n-list__main"
                    onClick={() => {
                      void navigate(`/musculation/routine/${encodeURIComponent(row.clientId)}`);
                    }}
                  >
                    <span className="n-list__name">{row.name}</span>
                    <span className="n-list__meta">
                      {poids.exercises === 0
                        ? 'aucun exercice'
                        : `${String(poids.exercises)} exercice${
                            poids.exercises > 1 ? 's' : ''
                          } · ${String(poids.sets)} série${poids.sets > 1 ? 's' : ''}`}
                    </span>
                  </button>

                  {/*
                    🔴 **Démarrer sans ouvrir** *(31/08/2026, demandé après
                    usage)*. Le geste le plus fréquent de cet écran — on arrive
                    en salle et on lance sa routine — coûtait deux appuis et un
                    changement d'écran, pour lire une page qu'on connaît par
                    cœur. Le détail garde son grand bouton « Démarrer la
                    séance » : il sert quand on vient **vérifier** avant de
                    lancer. Les deux voies mènent à la même mutation.

                    ⚠️ **Désactivé sur une routine vide**, comme le grand bouton
                    du détail : une routine sans bloc s'enregistre (c'est
                    `recipe_empty` transposé), mais il n'y a rien à dérouler.
                    Le refus est ici aussi, sinon ce raccourci contournerait la
                    seule garde que l'étape 5 avait posée à l'emploi.

                    ⚠️ **Cible de 44 px**, et une troisième colonne assumée sur
                    la ligne : l'étoile fait 44, le corps flexible garde
                    `min-width: 0`, et le nom passe à la ligne plutôt que de se
                    tronquer. Mesuré au banc sur 375 px.
                  */}
                  <Button
                    variant="ghost"
                    shape="circle"
                    className="n-routine-start"
                    aria-label={`Démarrer la séance « ${row.name} »`}
                    /* 🔴 Une seule séance à la fois — cf. `enCours` plus haut. */
                    disabled={row.blocks.length === 0 || start.isPending || enCours !== null}
                    onClick={() => {
                      start.mutate(row);
                    }}
                  >
                    <GlyphPlay />
                  </Button>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <Button
        variant="primary"
        size="lg"
        block
        onClick={() => {
          setDraft(emptyRoutineDraft());
          /*
           * 🔴 `clientId` tiré **à l'ouverture**, pas à l'enregistrement
           * (§2.3-b) : c'est l'adresse de la ressource, et l'avoir avant
           * l'écriture est ce qui rend la création composable hors ligne.
           */
          setEditing({ clientId: newClientId(), title: 'Nouvelle routine' });
        }}
      >
        Nouvelle routine
      </Button>

      {editor}
    </div>
  );
}
